import { Tree } from 'tree-wallet-client';
import { plantingDay } from '../Helper/DateHelper';

/**
 * Everything the native shell keeps for the page, in one file: the device check that decides whether any of it
 * exists, the offline trees list copy of Tree Map (what the list and the map filter on when there is no data connection), 
 * and the queue to be uploaded(the trees collected without a connection, waiting to be sent).
 *
 * Both live in SQLite behind a bridge (`TreeStoreBridge.kt`, `QueueStoreBridge.kt`), so the WebView's IndexedDB is
 * never opened in the app. A browser has no bridge: it keeps its trees in memory using IndexedDB, as it always did.
 */

// ---------------------------------------------------------------------------
// The native shell
// ---------------------------------------------------------------------------

// The Android or iOS shell loads this same bundle and marks itself in the user
// agent, which `shellUserAgent` in `MainActivity.kt` builds as "TreeWallet App <build> | WebView ...".
const APP_USER_AGENT = /^TreeWallet App /;

/** Whether the page is running inside the native shell rather than in a browser */
export function isNativeApp(): boolean {
  return typeof navigator !== 'undefined' && APP_USER_AGENT.test(navigator.userAgent);
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

/** What the shell exposes as `window.TwTreeStore` (see `TreeStoreBridge.kt`): the trees list copy, in SQLite */
interface TreeStoreBridge {
  upsert(rowsJson: string): void;
  setComplete(complete: boolean): void;
  isComplete(): boolean;
  count(): number;
  page(filterJson: string, page: number, pageSize: number): string;
  plantedDays(filterJson: string): string;
  /** What the map's source files hold: `{ url: { size, cachedAt } }` for the ones the shell has */
  geojsonInfo?(): string;
  geojson?(name: string, raw: boolean): string | null;
  writeGeoJson?(name: string, gzippedBase64: string, size: number): void;
  /** Claims the whole trees list copy for one read: false when another read already holds it */
  beginCopy?(): boolean;
  endCopy?(): void;
  /** Rows as `id\tcontract\tcreatedAtMs\tday\tnumber\tpayload` lines, plain or gzipped: no JSON to parse */
  upsertLines?(lines: string, gzipped: boolean): void;
  /** Drops what a finished read did not write: its `contractId` is 0 when the read took in every contract */
  prune?(contractId: number, lightTreesListOngoingCopy: number): number;
  /** Hands the space a rebuild freed back to the device, off the page's thread when the channel is there */
  compact?(): void;
}

// Present in the app and nowhere else: where it is, the WebView's IndexedDB is never opened for the trees list copy
function treeStore(): TreeStoreBridge | undefined {
  return typeof window === 'undefined' ? undefined : (window as any).TwTreeStore;
}

/** The message channel the shell opens (`StoreMessageListener.kt`): the same store, without blocking the page */
interface StoreChannel {
  postMessage(data: string): void;
  onmessage: ((event: { data: string }) => void) | null;
}

interface StoreAnswer {
  id: number;
  ok: boolean;
  value?: any;
  error?: string;
}

function storeChannel(): StoreChannel | undefined {
  return typeof window === 'undefined' ? undefined : (window as any).TwStoreAsync;
}

const pendingStoreCalls = new Map<number, { resolve: (value: any) => void; reject: (error: Error) => void }>();
let nextStoreCallId = 1;
let channelAttached = false;

// One handler for the whole channel: answers come back in their own time, matched by id
function attachChannel(): boolean {
  const channel = storeChannel();
  if (!channel) {
    return false;
  }

  if (!channelAttached) {
    channel.onmessage = (event) => {
      const answer = JSON.parse(String(event.data)) as StoreAnswer;
      const waiter = pendingStoreCalls.get(answer.id);
      if (!waiter) {
        return;
      }

      pendingStoreCalls.delete(answer.id);
      if (answer.ok) {
        waiter.resolve(answer.value);
      } else {
        waiter.reject(new Error(answer.error || 'store call failed'));
      }
    };
    channelAttached = true;
  }

  return true;
}

function callStore(op: string, args: { [key: string]: any }): Promise<any> {
  const channel = storeChannel();
  const id = nextStoreCallId++;

  return new Promise((resolve, reject) => {
    pendingStoreCalls.set(id, { resolve, reject });
    channel!.postMessage(JSON.stringify({ id, op, ...args }));
  });
}

/** One tree as the shell stores it: the columns it filters on, and the row the list renders */
interface StoreRow {
  id: number;
  contractId: number | null;
  createdAtMs: number;
  plantingDay: string | null;
  numberPlanted: number;
  /** The tree as text: the shell stores it without parsing it, which measured 44% faster than an object */
  payload: string;
}

// A call carries about this much JSON: big enough that the per-call overhead disappears, small enough that the
// page is not blocked for long on a slow device
const BRIDGE_BATCH_BYTES = 256 * 1024;

const toStoreRows = (trees: Tree[]): StoreRow[] => trees.map((tree) => {
  const createdAtMs = tree.createdAt ? new Date(tree.createdAt).getTime() : 0;

  return {
    id: Number(tree.id),
    contractId: tree.contractId === undefined || tree.contractId === null ? null : Number(tree.contractId),
    createdAtMs: Number.isNaN(createdAtMs) ? 0 : createdAtMs,
    plantingDay: tree.createdAt ? plantingDay(tree.createdAt) : null,
    numberPlanted: tree.numberPlanted || 1,
    payload: JSON.stringify(stripPhotos(tree)),
  };
});

// A photo's reference only: the picture itself is in the image cache, and the popup needs the url to ask for it,
// the id to key the carousel and the date to order it. The light trees list copy keeps the list's row, never the tree's photos and full data
const stripPhotos = (tree: Tree): Tree => {
  const copy = { ...tree };
  const photos = (tree.photos || [])
    .filter((photo) => photo?.imageUrl)
    .map((photo) => ({ id: photo.id, imageUrl: photo.imageUrl, createdAt: photo.createdAt }));

  if (photos.length) {
    copy.photos = photos as Tree['photos'];
  } else {
    delete copy.photos;
  }

  return copy;
};

// `id \t contract \t createdAtMs \t day \t number \t payload` per line: the shell splits instead of parsing JSON
const toLines = (rows: StoreRow[]): string => rows.map((row) => [
  row.id,
  row.contractId === null ? '' : row.contractId,
  row.createdAtMs,
  row.plantingDay === null ? '' : row.plantingDay,
  row.numberPlanted,
  row.payload,
].join('\t')).join('\n');

const gzipBase64 = async (text: string): Promise<string | null> => {
  const CompressionStreamCtor = (window as any).CompressionStream;
  if (!CompressionStreamCtor) {
    return null;
  }

  try {
    const gzipped = await new Response(
      new Blob([text]).stream().pipeThrough(new CompressionStreamCtor('gzip')),
    ).arrayBuffer();

    const bytes = new Uint8Array(gzipped);
    let binary = '';
    for (let index = 0; index < bytes.length; index += 0x8000) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(index, index + 0x8000)));
    }

    return btoa(binary);
  } catch (error) {
    console.warn('could not compress the trees, sending them as they are', error);
    return null;
  }
};

// Batches by size rather than by count: one tree and one tree hole are not the same amount of JSON
function batchesOf(rows: StoreRow[]): StoreRow[][] {
  const batches: StoreRow[][] = [];
  let batch: StoreRow[] = [];
  let size = 0;

  for (const row of rows) {
    // The row's own weight, without serialising it twice: the payload is nearly all of it
    const weight = row.payload.length + 120;

    if (batch.length > 0 && size + weight > BRIDGE_BATCH_BYTES) {
      batches.push(batch);
      batch = [];
      size = 0;
    }

    batch.push(row);
    size += weight;
  }

  if (batch.length > 0) {
    batches.push(batch);
  }

  return batches;
}

/**
 * Writes lines the fastest way the shell allows: over the message channel when it has one, on the blocking bridge
 * when it does not, and as JSON batches for a shell too old to know the line protocol.
 */
async function writeLines(rows: StoreRow[]): Promise<void> {
  const bridge = treeStore();
  if (!bridge || rows.length === 0) {
    return;
  }

  const lines = toLines(rows);
  const compressed = await gzipBase64(lines);
  const gzipped = compressed !== null;
  const text = gzipped ? compressed! : lines;

  try {
    if (attachChannel()) {
      try {
        await callStore('upsertLines', { lines: text, gzipped });
        return;
      } catch (error) {
        console.warn('the store channel did not answer, falling back to the blocking bridge', error);
      }
    }

    if (bridge.upsertLines) {
      bridge.upsertLines(text, gzipped);
      return;
    }

    // Older shell: the JSON entry points, one call per batch
    for (const batch of batchesOf(rows)) {
      bridge.upsert(JSON.stringify(batch));
    }
  } catch (error) {
    console.error('could not write the trees into the app store', { error });
  }
}

/**
 * Claims the trees list copy for the save job: pressing `Save Offline` twice must not download everything twice
 *
 * @return false when a save already holds it
 */
export async function claimTreesListCopy(): Promise<boolean> {
  const bridge = treeStore();
  if (!bridge) {
    return true;
  }

  try {
    // Older shells have no claim, where only the page can guard the read
    return bridge.beginCopy ? bridge.beginCopy() : true;
  } catch (error) {
    console.error('could not claim the app store', { error });
    return false;
  }
}

/** A save that gave up hands the trees list copy back, so the next press is not refused until the claim expires */
export async function releaseTreesListCopy(): Promise<void> {
  try {
    treeStore()?.endCopy?.();
  } catch (error) {
    console.warn('could not release the app store', { error });
  }
}

/** Trees list copy is completed: the filters can answer from it, offline included (see `Filters`) */
export async function endTreesListCopy(): Promise<void> {
  try {
    treeStore()?.setComplete(true);
  } catch (error) {
    console.error('could not mark the app store complete', { error });
  }
}

/**
 * Hands back the space a full save left in the write-ahead log: SQLite checkpoints by resetting the log, never by
 * truncating it, so the file keeps the size the save filled it to
 */
export async function compactTreesListCopy(): Promise<void> {
  try {
    if (attachChannel()) {
      await callStore('compact', {});
      return;
    }

    treeStore()?.compact?.();
  } catch (error) {
    console.warn('could not compact the app store', { error });
  }
}

/** Trees read one page at a time and kept as they land: an interrupted read loses nothing it has seen */
export async function mergeTrees(trees: Tree[]): Promise<void> {
  await writeLines(toStoreRows(trees));
}

/** Whether a trees list copy exists at all: without one there is nothing to filter when the connection is gone */
export async function hasTreesListCopy(): Promise<boolean> {
  try {
    return (treeStore()?.count() || 0) > 0;
  } catch (error) {
    console.error('could not read the app store', { error });
    return false;
  }
}

// False when the trees list copy is incomplete
async function isTreesListCopyComplete(): Promise<boolean> {
  try {
    return treeStore()?.isComplete() === true;
  } catch (error) {
    console.error('Could not copy the trees list', { error });
    return false;
  }
}

// ---------------------------------------------------------------------------
// The map's source files
// ---------------------------------------------------------------------------

// What the store holds per file, read once per load: a file whose text length did not change is not written again
let storedGeoJson: { [name: string]: { size: number; cachedAt: number } } | null = null;

const canInflate = typeof DecompressionStream !== 'undefined';

const inflateBase64 = async (base64: string): Promise<string> => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const inflated = await new Response(
    new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')),
  ).arrayBuffer();

  return new TextDecoder().decode(inflated);
};

// One attempt more, and how long to let a rebuild's VACUUM finish before it
const MAP_READ_ATTEMPTS = 2;
const MAP_READ_RETRY_DELAY = 400;

/**
 * One of the map's `/api/tree/geojsonfile` sources, from the shell's store. `null` when the shell does not hold it -
 * or has no store at all - which is what sends the caller to the network.
 */
export async function readGeoJsonFile(name: string): Promise<any | null> {
  if (!attachChannel()) {
    return null;
  }

  // A read landing while the store is busy fails instead of waiting, and the map used to re-download 1.6 MB over
  // it: one more attempt covers the few hundred milliseconds the store is held.
  for (let attempt = 1; ; attempt++) {
    try {
      const answer = (await callStore('geojson', { name, raw: canInflate })) as { raw: boolean; body: string } | null;

      if (!answer) {
        return null;
      }

      return JSON.parse(answer.raw ? await inflateBase64(answer.body) : answer.body);
    } catch (error) {
      if (attempt === MAP_READ_ATTEMPTS) {
        // Forgotten here: `writeGeoJsonFile` skips a file whose stored size already matches, so a body that cannot
        // be read would otherwise be downloaded again on every open and never written back
        if (storedGeoJson) {
          delete storedGeoJson[name];
        }

        console.warn('could not read the map data from the app store', { name, error: String((error as any)?.message || error) });
        return null;
      }

      await new Promise((resolve) => setTimeout(resolve, MAP_READ_RETRY_DELAY));
    }
  }
}

/** Keeps a source file the page just fetched, so the worker never has to hold it (see `service-worker.js`) */
export async function writeGeoJsonFile(name: string, text: string): Promise<void> {
  if (!attachChannel()) {
    return;
  }

  try {
    const held = await readGeoJsonHeld();

    // Nothing planted since the last read: what the store holds is already this file
    if (held[name] && held[name].size === text.length) {
      return;
    }

    const gzipped = await gzipBase64(text);
    if (gzipped === null) {
      return;
    }

    await callStore('writeGeoJson', { name, body: gzipped, size: text.length });
    held[name] = { size: text.length, cachedAt: Date.now() };
  } catch (error) {
    console.warn('could not keep the map data in the app store', { name, error });
  }
}

async function readGeoJsonHeld(): Promise<{ [name: string]: { size: number; cachedAt: number } }> {
  if (storedGeoJson !== null) {
    return storedGeoJson;
  }

  storedGeoJson = {};

  try {
    const info = (await callStore('geojsonInfo', {})) as { [name: string]: { size: number; cachedAt: number } };
    Object.keys(info || {}).forEach((name) => {
      storedGeoJson![name] = info[name];
    });
  } catch (error) {
    console.warn('could not read the map data the app store holds', { error });
  }

  return storedGeoJson;
}

/**
 * The shell holds the geojson responses in SQLite now, so the worker does not have to: it only keeps the compressed file, and the page reads it from the store
 */
export async function purgeCachedGeoJson(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }

  try {
    const cache = await caches.open('data-cache');
    const requests = await cache.keys();
    const stale = requests.filter((request) => new URL(request.url).pathname.startsWith('/api/tree/geojsonfile'));

    await Promise.all(stale.map((request) => cache.delete(request)));

    if (stale.length > 0) {
      console.log(`[store] removed ${stale.length} map files the worker had cached`);
    }
  } catch (error) {
    console.warn('could not remove the cached map files', { error });
  }
}

// ---------------------------------------------------------------------------
// Queries - the same answers the API gives, without the round trip
// ---------------------------------------------------------------------------

// The filter the map sends to the API (`TreeMapFilter` on the server side)
export interface TreesListCopyFilter {
  treeId?: any;
  contractId?: any;
  dateStart?: number | null;
  dateEnd?: number | null;
  sortField?: string;
  sortOrder?: string;
}

export interface TreesListCopyPage {
  data: Tree[];
  pager: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    /** Trees planted in the matched rows, not rows: a hole holds more than one tree */
    totalTreesPlanted: number;
  };
  /** The same figure on its own, as the API sends it in `extraData.treeCount` */
  treeCount: number;
}

const dayStart = (seconds: number): number => {
  const date = new Date(seconds * 1000);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

// `dateEnd` is inclusive on the API: it compares against the midnight *after* that day
const dayEnd = (seconds: number): number => {
  const date = new Date(seconds * 1000);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 1);
  return date.getTime();
};

const isSet = (value: any): boolean => value !== undefined && value !== null && String(value).trim() !== '';

// What the shell filters on: the day boundaries are worked out here, where the calendar's days live
const bridgeFilter = (filter: TreesListCopyFilter): string => JSON.stringify({
  treeId: isSet(filter.treeId) ? Number(filter.treeId) : null,
  contractId: isSet(filter.contractId) ? Number(filter.contractId) : null,
  fromMs: filter.dateStart ? dayStart(filter.dateStart) : null,
  untilMs: filter.dateEnd ? dayEnd(filter.dateEnd) : null,
  sortField: filter.sortField || 'createdAt',
  sortOrder: filter.sortOrder === 'asc' ? 'asc' : 'desc',
});

/** One tree of the trees list copy as the list rendered it: what the popup falls back to when the API cannot be reached */
export async function readTreesListCopyTree(treeId: number): Promise<Tree | null> {
  const bridge = treeStore();
  if (!bridge) {
    return null;
  }

  try {
    const answer = JSON.parse(bridge.page(bridgeFilter({ treeId }), 1, 1));
    const row = answer?.rows?.[0];

    return row ? JSON.parse(row) : null;
  } catch (error) {
    console.error('could not read the tree from the app store', { error });
    return null;
  }
}

/**
 * The page of trees a filter asks for, or `null` when nothing has been copied yet. The pager carries the
 * same keys as the API's, so the caller does not have to know where the rows came from.
 */
export async function readTreesListCopyPage(filter: TreesListCopyFilter, page?: number, pageSize?: number): Promise<TreesListCopyPage | null> {
  const bridge = treeStore();
  if (!bridge) {
    return null;
  }

  try {
    if (bridge.count() === 0) {
      return null;
    }

    // A copy that is still being written answers short, and a filter run on it under-reports: better the one
    // sentence "no data connection" than a list that quietly misses trees
    if (!(await isTreesListCopyComplete())) {
      return null;
    }

    const size = Math.max(1, Number(pageSize) || 25);
    const answer = JSON.parse(bridge.page(bridgeFilter(filter), Number(page) || 1, size));

    return {
      data: (answer.rows as string[]).map((row) => JSON.parse(row) as Tree),
      pager: {
        currentPage: answer.currentPage,
        pageSize: size,
        totalPages: answer.totalPages,
        totalRecords: answer.total,
        hasNextPage: answer.currentPage < answer.totalPages,
        hasPreviousPage: answer.currentPage > 1,
        totalTreesPlanted: answer.treeCount,
      },
      treeCount: answer.treeCount,
    };
  } catch (error) {
    console.error('could not read the app store', { error });
    return null;
  }
}

/**
 * How many trees stand on each day (`YYYY-MM-DD`) of the trees list copy: the days the Filters calendar highlights,
 * counted without a request.
 */
export async function readTreesListCopyPlantedDays(filter: TreesListCopyFilter): Promise<{ [day: string]: number }> {
  try {
    // Counted over the whole copy: a copy still being written would leave days out, and the Calendar would show
    // a contract with almost no planting days
    if (!(await isTreesListCopyComplete())) {
      return {};
    }

    return JSON.parse(treeStore()?.plantedDays(bridgeFilter(filter)) || '{}');
  } catch (error) {
    console.error('could not read the app store', { error });
    return {};
  }
}

// ---------------------------------------------------------------------------
// The upload queue - the trees collected without a connection
// ---------------------------------------------------------------------------

/** What the shell exposes as `window.TwQueueStore` (see `QueueStoreBridge.kt`): the queue, in SQLite */
interface QueueStoreBridge {
  open(): boolean;
  put(table: string, valueJson: string, key: string): string;
  get(table: string, key: string): string;
  getAll(table: string): string;
  getAllKeys(table: string): string;
  count(table: string): number;
  /** The queue's tree count (see `totalTrees`) */
  sumTrees?(table: string): number;
  delete(table: string, key: string): boolean;
  clear(table: string): void;
  /** Old shells only, where photos could not be posted as bytes: the row carries base64 instead */
  putPhoto?(name: string, base64: string): boolean;
}

// The two tables the shell keeps
const QUEUE_TABLE_TREES = 'trees';
const QUEUE_TABLE_VALIDATED = 'validated_queue';
// Older shell: the bytes of the photo inside the row
const QUEUE_BLOB_TAG = '__twBlob';
// Current: the row names a photo file the shell keeps beside the database (see `QueueStoreBridge.kt`)
const QUEUE_PHOTO_TAG = '__twPhoto';
// The path the shell answers a photo on (see `QueueStoreBridge.photoResponse`)
const PHOTO_ROUTE = '__queue-photo';

function queueStore(): QueueStoreBridge | undefined {
  return typeof window === 'undefined' ? undefined : (window as any).TwQueueStore;
}

const hasNativeQueue = (): boolean => !!queueStore();

const isBlob = (value: any): boolean => typeof Blob !== 'undefined' && value instanceof Blob;

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';

  // In chunks: `String.fromCharCode(...bytes)` on a photo of several hundred KB overflows the argument limit
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(index, index + 0x8000)));
  }

  return btoa(binary);
}

function base64ToDataUrl(base64: string, type: string): string {
  return `data:${type || 'application/octet-stream'};base64,${base64}`;
}

let photoCounter = 0;

/** `window.TwQueuePhotos` (`QueueMessageListener.kt`): the bytes of a photo, without encoding them */
interface PhotoChannel {
  postMessage(data: ArrayBuffer): void;
  onmessage: ((event: { data: string }) => void) | null;
}

function photoChannel(): PhotoChannel | undefined {
  return typeof window === 'undefined' ? undefined : (window as any).TwQueuePhotos;
}

// Writes waiting for their name to come back: photos are queued one after the other
const pendingPhotoWrites = new Map<string, (stored: boolean) => void>();
let photoChannelAttached = false;
const PHOTO_WRITE_TIMEOUT_MS = 5000;

function attachPhotoChannel(channel: PhotoChannel): void {
  if (photoChannelAttached) {
    return;
  }

  channel.onmessage = (event) => {
    const answer = String(event.data ?? '');
    // No name is a refusal, and the shell was working on the oldest write
    const name = answer || Array.from(pendingPhotoWrites.keys())[0];
    const settle = name ? pendingPhotoWrites.get(name) : undefined;

    if (!name || !settle) {
      return;
    }

    pendingPhotoWrites.delete(name);
    settle(answer !== '');
  };

  photoChannelAttached = true;
}

/**
 * Writes the photo with the shell, as bytes: `[4 bytes name length][name][photo]`. `false` when this shell cannot
 * take bytes, which is what sends the caller to the base64 row.
 */
function postPhotoBytes(name: string, bytes: Uint8Array): Promise<boolean> {
  const channel = photoChannel();

  if (!channel) {
    return Promise.resolve(false);
  }

  attachPhotoChannel(channel);

  const nameBytes = new TextEncoder().encode(name);
  const frame = new Uint8Array(4 + nameBytes.length + bytes.length);
  new DataView(frame.buffer).setUint32(0, nameBytes.length);
  frame.set(nameBytes, 4);
  frame.set(bytes, 4 + nameBytes.length);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pendingPhotoWrites.delete(name);
      resolve(false);
    }, PHOTO_WRITE_TIMEOUT_MS);

    pendingPhotoWrites.set(name, (stored) => {
      clearTimeout(timer);
      resolve(stored);
    });

    try {
      channel.postMessage(frame.buffer);
    } catch {
      pendingPhotoWrites.delete(name);
      clearTimeout(timer);
      resolve(false);
    }
  });
}

/** `q-<time>-<n>.<ext>`: unique per capture, and a name the shell accepts as it is */
function photoName(type: string): string {
  const extension = (type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '').toLowerCase() || 'bin';
  return `q-${Date.now().toString(36)}-${(photoCounter++).toString(36)}.${extension}`;
}

/**
 * Keeps a photo as its own file, the row holding only the name: `null` when the shell cannot take it, so the caller
 * falls back to the bytes inside the row.
 */
async function keepPhoto(blob: Blob, type: string): Promise<{ [key: string]: string } | null> {
  const store = queueStore();
  const name = photoName(type);
  const bytes = new Uint8Array(await blob.arrayBuffer());

  const stored = (await postPhotoBytes(name, bytes))
    || !!store?.putPhoto?.(name, await blobToBase64(blob));

  return stored ? { [QUEUE_PHOTO_TAG]: name, type } : null;
}

/** Where the WebView reads the file (`QueueStoreBridge.photoResponse`), at no cost to the page's memory */
function photoUrl(name: string): string {
  const origin = typeof location === 'undefined' ? '' : location.origin;
  return `${origin}/${PHOTO_ROUTE}/${encodeURIComponent(name)}`;
}

// A queued tree holds strings and one photo: the read keeps the shapes it knows and hands the rest through
async function encodeQueueValue(value: any): Promise<any> {
  if (isBlob(value)) {
    const type = (value as Blob).type || 'image/jpeg';
    const kept = await keepPhoto(value as Blob, type);

    return kept || { [QUEUE_BLOB_TAG]: await blobToBase64(value as Blob), type };
  }

  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => encodeQueueValue(item)));
  }

  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const encoded: { [key: string]: any } = {};

    for (const [key, item] of Object.entries(value)) {
      encoded[key] = await encodeQueueValue(item);
    }

    return encoded;
  }

  return value;
}

function decodeQueueValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map((item) => decodeQueueValue(item));
  }

  if (value && typeof value === 'object') {
    if (typeof value[QUEUE_PHOTO_TAG] === 'string') {
      // A url the shell answers from the file itself: listing the queue transfers no pixels at all
      return photoUrl(value[QUEUE_PHOTO_TAG]);
    }

    if (typeof value[QUEUE_BLOB_TAG] === 'string') {
      // A row written before photos had their own files
      return base64ToDataUrl(value[QUEUE_BLOB_TAG], value.type);
    }

    const decoded: { [key: string]: any } = {};

    Object.entries(value).forEach(([key, item]) => {
      decoded[key] = decodeQueueValue(item);
    });

    return decoded;
  }

  return value;
}

// Writes one row of the queue: the photo already has its own file, everything else travels as it is
async function writeQueueRow(table: string, value: any, key?: any): Promise<any> {
  const stored = queueStore()?.put(table, JSON.stringify(await encodeQueueValue(value)), key === undefined || key === null ? '' : String(key));

  if (!stored) {
    return undefined;
  }

  return table === QUEUE_TABLE_TREES ? Number(stored) : stored;
}

/**
 * The queue, with the operations `IndexedDb` offers: `Service/DBService` hands the app this one and a browser the
 * IndexedDB one, and nothing above it has to know which.
 */
export const nativeQueue = {
  isAvailable: hasNativeQueue,

  async openDb(): Promise<boolean> {
    return !!queueStore()?.open();
  },

  async getValue(table: string, id: any): Promise<any> {
    const raw = queueStore()?.get(table, String(id));
    return raw ? decodeQueueValue(JSON.parse(raw)) : null;
  },

  async getAllValue(table: string): Promise<any[]> {
    const rows = JSON.parse(queueStore()?.getAll(table) || '[]') as { value: string }[];
    return rows.map((row) => decodeQueueValue(JSON.parse(row.value)));
  },

  async getAllKeys(table: string): Promise<any[]> {
    return JSON.parse(queueStore()?.getAllKeys(table) || '[]');
  },

  async count(table: string): Promise<number> {
    return queueStore()?.count(table) || 0;
  },

  // The trees the queue holds, counted by the shell: counting them here means reading every row, and a row
  // is mostly a photo. `null` when the shell is older than the count
  async totalTrees(table: string): Promise<number | null> {
    const store = queueStore();
    if (!store || typeof store.sumTrees !== 'function') {
      return null;
    }

    return store.sumTrees(table);
  },

  // Only the trees a row holds: the photo next to them is what makes a read of the queue expensive
  async getNumber(table: string, id: any): Promise<number> {
    const raw = queueStore()?.get(table, String(id));
    const trees = parseInt(String(raw ? JSON.parse(raw).number : ''), 10);

    return Number.isFinite(trees) ? trees : 0;
  },

  async putValue(table: string, value: object, key?: any): Promise<any> {
    return writeQueueRow(table, value, key);
  },

  async putBulkValue(table: string, values: object[]): Promise<any[]> {
    for (const value of values) {
      await nativeQueue.putValue(table, value);
    }

    return nativeQueue.getAllValue(table);
  },

  async deleteValue(table: string, id: any): Promise<any> {
    return queueStore()?.delete(table, String(id)) ? id : null;
  },

  /**
   * Empties the queue. Refused while it holds trees: an unsent tree and its photo exist nowhere else, so wiping
   * them takes an explicit `force` (see `DBService.deleteDb`).
   */
  async deleteDb(force = false): Promise<boolean> {
    if (!force && (queueStore()?.count(QUEUE_TABLE_TREES) || 0) > 0) {
      console.error('refused to empty the queue: it still holds trees waiting to be uploaded');
      return false;
    }

    queueStore()?.clear(QUEUE_TABLE_TREES);
    queueStore()?.clear(QUEUE_TABLE_VALIDATED);

    return true;
  },

  /** The paged reads `TreeRepository` does: the same cursor an IndexedDB store hands out */
  async getCursor(table: string): Promise<any> {
    const keys = await nativeQueue.getAllKeys(table);

    const step = async (position: number): Promise<any> => {
      if (position >= keys.length) {
        return null;
      }

      const key = keys[position];

      return {
        key,
        value: await nativeQueue.getValue(table, key),
        continue: () => step(position + 1),
      };
    };

    return step(0);
  },
};
